import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2, AlertTriangle , Trash2 } from 'lucide-react';
import { matApi } from '../../api/materialthequeApi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/materialtheque`;

const CELL = {
  explicit: { label: 'Épinglé à la pièce', color: 'var(--c-gold)', bg: 'rgba(217,160,91,0.14)' },
  default:  { label: 'Retenu du rôle (défaut)', color: '#5EEAD4', bg: 'rgba(94,234,212,0.10)' },
  slot:     { label: 'Slot du plan renseigné', color: '#CBD5E1', bg: 'rgba(148,163,184,0.12)' },
  missing:  { label: 'Manquant — l\u2019IA improvisera', color: '#FCA5A5', bg: 'rgba(239,68,68,0.14)' },
};

/**
 * RoomMatrix — V15.12 · Vue « Par pièce » : pièces extraites des lots du
 * projet × rôles de finition attendus, chaque case résolue et réaffectable.
 */
export const RoomMatrix = ({ projectId, refreshKey = 0, onChanged }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/projects/${projectId}/room-matrix`, { withCredentials: true })
      .then((r) => setData(r.data)).catch(() => setData(null));
  }, [projectId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const unpin = async (room, cell) => {
    const prev = cell.material && [...cell.candidates, ...(cell.all_materials || [])].find((c) => c.material_id === cell.material.material_id);
    if (!prev) return;
    const roles = { ...(prev.room_roles || {}) };
    delete roles[room.label];
    await matApi.updateMaterial(prev.material_id, { rooms: prev.rooms.filter((r) => r !== room.label), room_roles: roles });
  };

  const removeMaterial = async (mat) => {
    if (!window.confirm(`Supprimer définitivement « ${mat.name} » de la matériauthèque ? Elle disparaîtra de toutes les pièces et des listes par rôle.`)) return;
    try { await matApi.deleteMaterial(mat.material_id); toast.success(`« ${mat.name} » supprimée`); load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Suppression impossible'); }
  };

  const assign = async (room, cell, materialId) => {
    setBusy(`${room.label}:${cell.role}`);
    try {
      if (materialId === '__default__') {
        if (cell.status === 'explicit') await unpin(room, cell);
        toast.success(`${room.label} · ${cell.role_label} → défaut du rôle`);
        load(); onChanged?.();
        return;
      }
      const cand = [...cell.candidates, ...(cell.all_materials || [])].find((c) => c.material_id === materialId);
      if (!cand) return;
      // retire l'ancien épinglage de cette case
      if (cell.status === 'explicit' && cell.material?.material_id !== materialId) await unpin(room, cell);
      const rooms = cand.rooms.includes(room.label) ? cand.rooms : [...cand.rooms, room.label];
      await matApi.updateMaterial(materialId, { rooms, room_roles: { ...(cand.room_roles || {}), [room.label]: cell.role } });
      toast.success(`${cand.name} → ${room.label} (${cell.role_label.toLowerCase()})`);
      load(); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Affectation impossible');
    } finally {
      setBusy(null);
    }
  };

  if (!data) return <div className="px-5 py-3 text-[11px]" style={{ color: 'var(--c-text-muted)' }}><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Lecture des pièces du projet…</div>;
  if (data.rooms.length === 0) {
    return (
      <div className="px-5 py-3 text-[12px]" style={{ color: 'var(--c-text-muted)' }} data-testid="room-matrix-empty">
        Aucune pièce tracée dans les lots de ce projet — dessinez les pièces dans Édition Plan › 3 · Pièces pour obtenir la vue par pièce.
      </div>
    );
  }
  return (
    <div className="px-5 py-3 space-y-2" data-testid="room-matrix">
      <div className="flex items-center gap-3 text-[11px]" style={{ color: 'var(--c-text-2)' }}>
        <span>{data.rooms.length} pièce{data.rooms.length > 1 ? 's' : ''} distincte{data.rooms.length > 1 ? 's' : ''} dans les lots</span>
        {data.missing_count > 0 && (
          <span className="inline-flex items-center gap-1" style={{ color: '#FCA5A5' }} data-testid="room-matrix-missing-count">
            <AlertTriangle className="w-3 h-3" /> {data.missing_count} case{data.missing_count > 1 ? 's' : ''} manquante{data.missing_count > 1 ? 's' : ''}
          </span>
        )}
        {Object.entries(CELL).map(([k, v]) => (
          <span key={k} className="inline-flex items-center gap-1 ml-1"><span className="w-2 h-2 rounded-full" style={{ background: v.color }} />{v.label}</span>
        ))}
      </div>
      <div className="grid gap-1.5">
        {data.rooms.map((room) => (
          <div key={`${room.type}:${room.label}`} className="grid grid-cols-12 gap-2 items-start rounded-md px-2 py-1.5"
               style={{ background: 'rgba(255,255,255,0.02)' }} data-testid={`room-row-${room.label}`}>
            <div className="col-span-3">
              <div className="text-[12.5px] font-medium" style={{ color: 'var(--c-text)' }}>{room.label}</div>
              <div className="text-[10px]" style={{ color: 'var(--c-text-muted)' }}>{room.type} · {room.lots.join(', ')}</div>
            </div>
            <div className="col-span-9 flex flex-wrap gap-2">
              {room.cells.map((cell) => {
                const st = CELL[cell.status];
                const key = `${room.label}:${cell.role}`;
                return (
                  <div key={cell.role} className="min-w-[200px] flex-1 rounded px-2 py-1" style={{ background: st.bg, border: `1px solid ${st.color}40` }}
                       data-testid={`room-cell-${room.label}-${cell.role}`} data-status={cell.status}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] uppercase tracking-wider" style={{ color: st.color }}>{cell.role_label}</span>
                      <span className="inline-flex items-center gap-1">
                        {busy === key && <Loader2 className="w-3 h-3 animate-spin" style={{ color: st.color }} />}
                        {cell.status === 'explicit' && <button type="button" onClick={() => assign(room, cell, '__default__')} className="text-[10px] text-gray-400 hover:text-white" title="Retirer l'épinglage de cette pièce (revient au défaut du rôle)" data-testid={`room-cell-unpin-${room.label}-${cell.role}`}>↩</button>}
                        {cell.material && <button type="button" onClick={() => removeMaterial(cell.material)} className="text-gray-500 hover:text-rose-300" title={`Supprimer définitivement la référence « ${cell.material.name} » de la matériauthèque (toutes pièces)`} data-testid={`room-cell-delete-${room.label}-${cell.role}`}><Trash2 className="w-3 h-3" /></button>}
                      </span>
                    </div>
                    <select value={cell.status === 'explicit' ? cell.material.material_id : '__auto__'}
                            onChange={(e) => e.target.value !== '__auto__' && assign(room, cell, e.target.value)}
                            disabled={busy === key}
                            className="mt-0.5 w-full bg-black/30 border rounded px-1.5 py-0.5 text-[11.5px] outline-none"
                            style={{ borderColor: `${st.color}55`, color: 'var(--c-text)' }}
                            data-testid={`room-cell-select-${room.label}-${cell.role}`}>
                      <option value="__auto__">
                        {cell.status === 'explicit' ? `📌 ${cell.material.name}`
                          : cell.material ? `${cell.status === 'default' ? '★ ' : ''}${cell.material.name}${cell.status === 'default' ? ' (défaut du rôle)' : ''}`
                          : cell.candidates.length > 0 ? '— Choisir un matériau pour cette pièce —'
                          : '— Manquant : choisir une texture de remplacement —'}
                      </option>
                      {cell.status === 'explicit' && <option value="__default__">↩ Revenir au défaut du rôle</option>}
                      {cell.candidates.length > 0 && (
                        <optgroup label={`${cell.role_label} — matériaux du rôle`}>
                          {cell.candidates.map((c) => (
                            <option key={c.material_id} value={c.material_id}>{c.is_retained ? '★ ' : ''}{c.name}{c.finish ? ` — ${c.finish}` : ''}</option>
                          ))}
                        </optgroup>
                      )}
                      {(cell.all_materials || []).length > 0 && (
                        <optgroup label="Remplacement — autres matériaux du projet">
                          {cell.all_materials.map((c) => (
                            <option key={c.material_id} value={c.material_id}>{c.category ? `[${c.category}] ` : ''}{c.name}{c.finish ? ` — ${c.finish}` : ''}</option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                    {cell.status === 'missing' && cell.candidates.length === 0 && (
                      <div className="text-[10px] italic mt-0.5" style={{ color: st.color }}>Aucun matériau de ce type — choisissez un remplacement ci-dessus ou ajoutez-en (Enrichir / Ajouter).</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RoomMatrix;
